'use client'

/**
 * /embed/energy-cost-calculator — the energy cost calculator as a widget
 * (FRIDGE-356). Same two inputs and the SAME arithmetic as
 * /tools/energy-cost-calculator: both call lib/tools/energy-cost.ts, so the
 * widget cannot drift from the tool. Records nothing the visitor types (no
 * capture in this component).
 */
import { useMemo, useState } from 'react'
import {
  ENERGY_COST_DEFAULTS,
  calculateEnergyCost,
  energyCostAnswer,
} from '@/lib/tools/energy-cost'

const INPUT_CLASS =
  'w-full pl-3 pr-16 py-2.5 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white text-gray-900 tabular-nums'
const LABEL_CLASS = 'block text-[10px] font-bold uppercase tracking-[0.12em] text-gray-700 mb-1'

const usd = (n: number, digits: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`

export function EnergyCostCalculatorWidget() {
  const [kwh, setKwh] = useState<string>(ENERGY_COST_DEFAULTS.kwhPerYear)
  const [rate, setRate] = useState<string>(ENERGY_COST_DEFAULTS.rateCentsPerKwh)
  const results = useMemo(() => calculateEnergyCost(kwh, rate), [kwh, rate])
  const answer = energyCostAnswer(kwh, rate)

  return (
    <div className="grid gap-4 @min-[420px]:grid-cols-2 @min-[420px]:items-stretch">
      <div className="space-y-3">
        <div>
          <label htmlFor="embed-energy-kwh" className="block">
            <span className={LABEL_CLASS}>Annual kWh usage</span>
            <span className="relative block">
            <input
              id="embed-energy-kwh"
              // The visible label sits in the sibling <span>; this names the control
              // explicitly so its accessible name never depends on that structure.
              aria-label="Annual kWh usage"
              type="number"
              inputMode="decimal"
              min={0}
              value={kwh}
              onChange={(e) => setKwh(e.target.value)}
              className={INPUT_CLASS}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-gray-500">
              kWh/yr
            </span>
            </span>
          </label>
          <p className="m-0 mt-1 text-[10px] text-gray-500">Use annual kWh from the appliance EnergyGuide label.</p>
        </div>
        <div>
          <label htmlFor="embed-energy-rate" className="block">
            <span className={LABEL_CLASS}>Electricity rate</span>
            <span className="relative block">
            <input
              id="embed-energy-rate"
              // The visible label sits in the sibling <span>; this names the control
              // explicitly so its accessible name never depends on that structure.
              aria-label="Electricity rate"
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className={INPUT_CLASS}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-gray-500">
              ¢/kWh
            </span>
            </span>
          </label>
          <p className="m-0 mt-1 text-[10px] text-gray-500">From your latest bill: total dollars ÷ total kWh</p>
        </div>
      </div>

      <div
        className="rounded-xl p-4 text-white flex flex-col justify-between"
        style={{ background: 'linear-gradient(135deg, #0469f0 0%, #0053c4 100%)' }}
        aria-live="polite"
      >
        <p className="m-0 text-[10px] font-bold uppercase tracking-[0.14em] text-white/70">Your annual cost</p>
        <p className="m-0 mt-1 font-black leading-none tracking-[-0.04em] text-[44px] tabular-nums">
          {usd(results.yearly, 0)}
          <span className="ml-1.5 text-[16px] font-black tracking-tight text-white/60">/yr</span>
        </p>
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            { l: 'Per day', v: usd(results.daily, 2) },
            { l: 'Per month', v: usd(results.monthly, 2) },
            { l: 'Per decade', v: usd(results.decade, 0) },
          ].map((s) => (
            <div key={s.l} className="rounded-lg p-2 bg-white/10 border border-white/15 min-w-0">
              <p className="m-0 text-[9px] uppercase tracking-[0.12em] text-white/60 font-bold">{s.l}</p>
              <p className="m-0 text-[14px] font-black tracking-tight tabular-nums truncate">{s.v}</p>
            </div>
          ))}
        </div>
        <p className="m-0 mt-2.5 text-[11px] text-white/85 leading-snug">
          {answer ?? 'Enter positive annual kWh and electricity rate values to estimate cost.'}
          {' '}The starting values are editable examples. No replacement savings or national average is inferred.
        </p>
      </div>
    </div>
  )
}
